import React, { useCallback, useEffect, useRef, useState } from "react";
import {
    Box,
    ButtonBase,
    Stack,
    Typography,
    useTheme,
} from "@mui/material";
import { alpha, darken, lighten } from "@mui/material/styles";
import { useNavigate, useParams } from "react-router-dom";
import EventRounded from "@mui/icons-material/EventRounded";
import ChatRounded from "@mui/icons-material/ChatRounded";
import PersonRounded from "@mui/icons-material/PersonRounded";
import CardMembershipRounded from "@mui/icons-material/CardMembershipRounded";
import PriorityHighRounded from "@mui/icons-material/PriorityHighRounded";
import AccessTimeRounded from "@mui/icons-material/AccessTimeRounded";
import MessageRounded from "@mui/icons-material/MessageRounded";
import CheckCircleRounded from "@mui/icons-material/CheckCircleRounded";
import CancelRounded from "@mui/icons-material/CancelRounded";
import WarningAmberRounded from "@mui/icons-material/WarningAmberRounded";
import EmailRounded from "@mui/icons-material/EmailRounded";
import PhoneRounded from "@mui/icons-material/PhoneRounded";
import Notification, { NotificationType } from "../../api/Notification";
import Booking, { BookingState } from "../../api/Booking";
import { useCookies } from "react-cookie";
import { parseWithOffset } from "../../utils/dayjsUtils";
import ColoredIcon from "../ColoredIcon";
import BackIcon from "../../img/Arrow_Left.svg";
import BottomButton from "../BottomButton";
import { FullScreenLoader } from "../FullScreenLoader";
import { FONTS, CARD_STYLES } from "../../constants/styles";

const NOISE_TEXTURE = `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`;

const getTypeIcon = (type: NotificationType, fontSize = 32) => {
    const sx = { fontSize, color: "white" };
    switch (type) {
        case NotificationType.Subscription:
            return <CardMembershipRounded sx={sx} />;
        case NotificationType.Booking:
            return <EventRounded sx={sx} />;
        case NotificationType.Chat:
            return <ChatRounded sx={sx} />;
        case NotificationType.User:
            return <PersonRounded sx={sx} />;
        case NotificationType.ConfirmationReview:
            return <PriorityHighRounded sx={sx} />;
        default:
            return <EventRounded sx={sx} />;
    }
};

const getTypeTitle = (type: NotificationType) => {
    switch (type) {
        case NotificationType.Subscription:
            return "Subscription";
        case NotificationType.Booking:
            return "Booking";
        case NotificationType.Chat:
            return "Chat";
        case NotificationType.User:
            return "User";
        case NotificationType.ConfirmationReview:
            return "Attention Required";
        default:
            return "Notification";
    }
};

const getTypePillLabel = (type: NotificationType) => {
    switch (type) {
        case NotificationType.Subscription:
            return "Subscription Update";
        case NotificationType.Booking:
            return "Booking Update";
        case NotificationType.Chat:
            return "New Message";
        case NotificationType.User:
            return "Client Update";
        case NotificationType.ConfirmationReview:
            return "Action Needed";
        default:
            return "Notification";
    }
};

const getCTAText = (type: NotificationType) => {
    switch (type) {
        case NotificationType.Chat:
            return "Go to Chat";
        case NotificationType.Booking:
        case NotificationType.ConfirmationReview:
            return "View Booking";
        case NotificationType.Subscription:
            return "View Subscription";
        case NotificationType.User:
            return "View User";
        default:
            return "View";
    }
};

const NotificationDetails = () => {
    const [cookies] = useCookies(["uuid", "brand_id", "location_id"]);
    const { uuid, brand_id, location_id } = cookies;

    const { notificationKey } = useParams<{ notificationKey: string }>();
    const navigate = useNavigate();
    const theme = useTheme();
    const primary = theme.palette.primary.main;

    const [notification, setNotification] = useState<
        Notification | undefined
    >();
    const [bookingInfo, setBookingInfo] = useState<Booking | undefined>();
    const [confirmed, setConfirmed] = useState(false);
    const [isCanceled, setIsCanceled] = useState(false);

    /* ── Scroll tracking ── */
    const scrollRef = useRef<HTMLDivElement>(null);
    const heroRef = useRef<HTMLDivElement>(null);
    const [scrolledPastHero, setScrolledPastHero] = useState(false);

    const handleScroll = useCallback(() => {
        const el = scrollRef.current;
        const heroEl = heroRef.current;
        if (!el || !heroEl) return;
        setScrolledPastHero(el.scrollTop > heroEl.offsetHeight - 60);
    }, []);

    const needsReview =
        notification?.type === NotificationType.ConfirmationReview &&
        bookingInfo &&
        !isCanceled &&
        !confirmed &&
        bookingInfo.miaCandidate === true;

    const isActiveAndConfirmed = confirmed && !isCanceled;

    const onBack = useCallback(() => {
        if (window.history.length > 1) {
            navigate(-1);
        } else {
            navigate("/notifications");
        }
    }, [navigate]);

    const fetchBookingIfNeeded = useCallback(
        async (notif: Notification) => {
            if (
                notif.type === NotificationType.ConfirmationReview ||
                notif.type === NotificationType.Booking
            ) {
                try {
                    const booking = await Booking.info(
                        uuid,
                        brand_id,
                        location_id,
                        notif.object,
                        true
                    );
                    if (booking) {
                        setBookingInfo(booking);
                        setConfirmed(booking.confirmed);
                        setIsCanceled(booking.state === BookingState.Cancel);
                    }
                } catch (err) {
                    console.error("Failed to fetch booking info", err);
                }
            }
        },
        [uuid, brand_id, location_id]
    );

    useEffect(() => {
        if (!uuid || !brand_id || !notificationKey) {
            window.location.href = "/";
            return;
        }
        Notification.info(uuid, brand_id, notificationKey)
            .then(async (res) => {
                setNotification(res);
                if (res) fetchBookingIfNeeded(res);
            })
            .catch(console.error);
        Notification.viewed(uuid, brand_id, notificationKey);
    }, [uuid, brand_id, notificationKey, fetchBookingIfNeeded]);

    const onConfirm = useCallback(() => {
        if (!bookingInfo || confirmed) return;
        setConfirmed(true);
        Booking.updateConfirmed(
            uuid,
            brand_id,
            location_id,
            bookingInfo.key,
            true
        ).catch((error) => {
            console.error("Error confirming booking:", error);
            setConfirmed(false);
            alert("Failed to confirm the booking. Please try again.");
        });
    }, [uuid, brand_id, location_id, bookingInfo, confirmed]);

    const onCancel = useCallback(() => {
        if (!bookingInfo) return;
        Booking.updateState(
            uuid,
            brand_id,
            location_id,
            bookingInfo.key,
            BookingState.Cancel
        )
            .then(() => setIsCanceled(true))
            .catch((error) => {
                console.error("Error canceling booking:", error);
                alert("Failed to cancel the booking. Please try again.");
            });
    }, [uuid, brand_id, location_id, bookingInfo]);

    const handleAction = useCallback(() => {
        if (!notification) return;
        switch (notification.type) {
            case NotificationType.Subscription:
                navigate("/menu/subscriptions");
                break;
            case NotificationType.Booking:
            case NotificationType.ConfirmationReview:
                navigate(`/calendar/appointment/${notification.object}`);
                break;
            case NotificationType.Chat:
                navigate(`/chats/${notification.object}`);
                break;
            case NotificationType.User:
                navigate(`/users/info/${notification.object}`);
                break;
            default:
                console.error("Unknown notification type");
        }
    }, [notification, navigate]);

    if (!notification) return <FullScreenLoader />;

    const title = getTypeTitle(notification.type);
    const iconBoxSx = {
        width: 32,
        height: 32,
        borderRadius: "10px",
        bgcolor: alpha(primary, 0.1),
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flex: "0 0 auto",
    };

    return (
        <Stack
            ref={scrollRef}
            onScroll={handleScroll}
            flex={1}
            sx={{ overflowY: "auto", overflowX: "hidden", bgcolor: "white" }}
        >
            {/* ── Hero ── */}
            <Stack
                ref={heroRef}
                alignItems="center"
                justifyContent="center"
                sx={{
                    width: "100%",
                    minHeight: 120,
                    position: "relative",
                    zIndex: 0,
                    background: `linear-gradient(180deg, ${lighten(primary, 0.08)} 0%, ${darken(primary, 0.08)} 100%)`,
                    transition: "background 0.3s ease",
                    pt: "40px",
                    pb: 4,
                    "&::after": {
                        content: '""',
                        position: "absolute",
                        inset: 0,
                        opacity: 0.04,
                        backgroundImage: NOISE_TEXTURE,
                        backgroundRepeat: "repeat",
                        backgroundSize: "128px 128px",
                        pointerEvents: "none",
                    },
                }}
            >
                <ButtonBase
                    onClick={onBack}
                    sx={{
                        position: "absolute", top: 12, left: 10, zIndex: 2,
                        width: 36, height: 36, borderRadius: "50%",
                        bgcolor: "rgba(60,60,60,0.35)",
                        backdropFilter: "blur(16px)",
                        WebkitBackdropFilter: "blur(16px)",
                        border: "1px solid rgba(255,255,255,0.2)",
                        display: "flex", alignItems: "center", justifyContent: "center",
                    }}
                >
                    <ColoredIcon icon={BackIcon} color="white" size="28px" />
                </ButtonBase>

                <Box
                    sx={{
                        width: 64,
                        height: 64,
                        borderRadius: "50%",
                        bgcolor: "rgba(60,60,60,0.3)",
                        backdropFilter: "blur(16px)",
                        WebkitBackdropFilter: "blur(16px)",
                        border: "1px solid rgba(255,255,255,0.15)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mb: 1,
                    }}
                >
                    {getTypeIcon(notification.type)}
                </Box>

                <Box
                    sx={{
                        bgcolor: "rgba(60,60,60,0.35)",
                        backdropFilter: "blur(16px)",
                        WebkitBackdropFilter: "blur(16px)",
                        border: "1px solid rgba(255,255,255,0.2)",
                        borderRadius: "16px",
                        px: 1.5,
                        py: 0.5,
                    }}
                >
                    <Typography fontSize="12px" fontWeight={500} color="white">
                        {getTypePillLabel(notification.type)}
                    </Typography>
                </Box>
            </Stack>

            {/* ── Sticky Toolbar ── */}
            <Box
                sx={{
                    position: "sticky",
                    top: 0,
                    zIndex: 4,
                    bgcolor: "white",
                    borderTopLeftRadius: "24px",
                    borderTopRightRadius: "24px",
                    mt: "-24px",
                    pt: 1.5,
                }}
            >
                <Stack
                    direction="row"
                    alignItems="center"
                    sx={{
                        maxHeight: scrolledPastHero ? 52 : 0,
                        opacity: scrolledPastHero ? 1 : 0,
                        overflow: "hidden",
                        transition: "max-height 0.25s ease, opacity 0.2s ease",
                        px: 1,
                        borderBottom: scrolledPastHero
                            ? "1px solid rgba(0,0,0,0.06)"
                            : "none",
                    }}
                >
                    <ButtonBase onClick={onBack} sx={{ p: 0.5 }}>
                        <ColoredIcon
                            icon={BackIcon}
                            color={primary}
                            size="28px"
                        />
                    </ButtonBase>
                    <Typography
                        fontFamily={FONTS.display}
                        fontSize="16px"
                        fontWeight={600}
                        ml={0.5}
                        noWrap
                        sx={{
                            flex: 1,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                        }}
                    >
                        {title}
                    </Typography>
                </Stack>
            </Box>

            {/* ── Content ── */}
            <Stack
                sx={{
                    position: "relative",
                    zIndex: 1,
                    bgcolor: "white",
                    boxSizing: "border-box",
                    px: 2,
                    pt: 0.5,
                    pb: "calc(4vh + 90px)",
                    "@keyframes fadeSlideIn": {
                        from: { opacity: 0, transform: "translateY(12px)" },
                        to: { opacity: 1, transform: "translateY(0)" },
                    },
                    animation: "fadeSlideIn 0.3s ease-out",
                }}
                spacing={2}
            >
                {/* ── Status banner (if applicable) ── */}
                {isActiveAndConfirmed && (
                    <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1}
                        sx={{
                            p: 1.5,
                            borderRadius: "14px",
                            bgcolor: alpha(theme.palette.success.main, 0.08),
                        }}
                    >
                        <CheckCircleRounded
                            sx={{
                                fontSize: 20,
                                color: theme.palette.success.main,
                            }}
                        />
                        <Typography
                            fontWeight={600}
                            fontSize="14px"
                            color={theme.palette.success.main}
                        >
                            Booking Confirmed
                        </Typography>
                    </Stack>
                )}
                {isCanceled && (
                    <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1}
                        sx={{
                            p: 1.5,
                            borderRadius: "14px",
                            bgcolor: alpha(theme.palette.error.main, 0.08),
                        }}
                    >
                        <CancelRounded
                            sx={{
                                fontSize: 20,
                                color: theme.palette.error.main,
                            }}
                        />
                        <Typography
                            fontWeight={600}
                            fontSize="14px"
                            color={theme.palette.error.main}
                        >
                            Booking Canceled
                        </Typography>
                    </Stack>
                )}

                {/* ── Action needed banner ── */}
                {needsReview && (
                    <Stack
                        sx={{
                            ...CARD_STYLES,
                            bgcolor: alpha(theme.palette.warning.main, 0.06),
                        }}
                        spacing={1.5}
                    >
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <Box
                                sx={{
                                    ...iconBoxSx,
                                    bgcolor: alpha(
                                        theme.palette.warning.main,
                                        0.12
                                    ),
                                }}
                            >
                                <WarningAmberRounded
                                    sx={{
                                        fontSize: 18,
                                        color: theme.palette.warning.dark,
                                    }}
                                />
                            </Box>
                            <Typography
                                fontWeight={600}
                                fontSize="14px"
                                color={theme.palette.warning.dark}
                            >
                                Client hasn't confirmed
                            </Typography>
                        </Stack>
                        {bookingInfo?.contacts?.email && (
                            <Stack
                                direction="row"
                                alignItems="center"
                                spacing={1}
                                ml={5.5}
                            >
                                <EmailRounded
                                    sx={{
                                        fontSize: 16,
                                        color: "text.secondary",
                                    }}
                                />
                                <Typography
                                    fontSize="13px"
                                    color="text.secondary"
                                >
                                    {bookingInfo.contacts.email}
                                </Typography>
                            </Stack>
                        )}
                        {bookingInfo?.contacts?.phone && (
                            <Stack
                                direction="row"
                                alignItems="center"
                                spacing={1}
                                ml={5.5}
                            >
                                <PhoneRounded
                                    sx={{
                                        fontSize: 16,
                                        color: "text.secondary",
                                    }}
                                />
                                <Typography
                                    fontSize="13px"
                                    color="text.secondary"
                                >
                                    {bookingInfo.contacts.phone}
                                </Typography>
                            </Stack>
                        )}
                    </Stack>
                )}

                {/* ── MESSAGE section ── */}
                <Typography
                    fontSize="11px"
                    fontWeight={600}
                    color="text.secondary"
                    textTransform="uppercase"
                    letterSpacing="0.5px"
                    mb={-1}
                >
                    Message
                </Typography>
                <Stack sx={CARD_STYLES} spacing={2}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <Box sx={iconBoxSx}>
                            <MessageRounded
                                sx={{ fontSize: 18, color: primary }}
                            />
                        </Box>
                        <Typography
                            fontWeight={400}
                            fontSize="12px"
                            lineHeight="18px"
                            color="text.secondary"
                        >
                            {title}
                        </Typography>
                    </Stack>
                    <Typography
                        fontSize="15px"
                        lineHeight="23px"
                        color="text.primary"
                    >
                        {notification.message}
                    </Typography>
                    <Stack direction="row" alignItems="center" spacing={0.75}>
                        <AccessTimeRounded
                            sx={{ fontSize: 14, color: "text.disabled" }}
                        />
                        <Typography
                            fontSize="12px"
                            color="text.disabled"
                        >
                            {parseWithOffset(notification.created).format(
                                "dddd, MMMM D, YYYY · HH:mm"
                            )}
                        </Typography>
                    </Stack>
                </Stack>

                {/* ── Booking review actions (inline) ── */}
                {needsReview && (
                    <Stack direction="row" spacing={1.5}>
                        <Box sx={{ flex: 1, display: "flex", justifyContent: "center" }}>
                            <BottomButton
                                onClick={onConfirm}
                                noLoading
                                color="primary"
                                width="100%"
                            >
                                Confirm
                            </BottomButton>
                        </Box>
                        <Box sx={{ flex: 1, display: "flex", justifyContent: "center" }}>
                            <BottomButton
                                onClick={onCancel}
                                noLoading
                                color="outlined"
                                width="100%"
                            >
                                Cancel
                            </BottomButton>
                        </Box>
                    </Stack>
                )}
            </Stack>

            {/* ── Sticky CTA at bottom ── */}
            <Box sx={{ display: "flex", justifyContent: "center" }}>
                <BottomButton
                    onClick={handleAction}
                    noLoading
                    sticky
                >
                    {getCTAText(notification.type)}
                </BottomButton>
            </Box>
        </Stack>
    );
};

export default NotificationDetails;
